import { useEffect, useState } from 'react';
import { Box, Column, Typography } from '@tempmee/react';
import { type Control, useFormContext, useWatch, type UseFormWatch } from 'react-hook-form';
import { TipBanner } from '~/components/shared/TipBanner';
import { useHasValidPaymentMethod } from '~/hooks/useHasValidPaymentMethod';
import { PaymentLockedBanner } from '../common';
import { type IShiftDetailsFormValues } from '../ShiftDetails/ShiftDetailsForm/useShiftDetailsForm';
import { AutoConfirmField } from './AutoConfirmField';
import { InviteField } from './InviteField';
import { InviteSubCard } from './InviteSubCard';

interface ISpeedUpCoverageSectionProps {
  control: Control<IShiftDetailsFormValues>;
  watch: UseFormWatch<IShiftDetailsFormValues>;
}

const INVITE_TOOLTIP = (
  <Typography as="span" bold color="#FFFFFF">
    Please note, invites will not be delivered to professionals on requested dates in which they are
    unavailable.
  </Typography>
);

const AUTO_CONFIRM_TOOLTIP = (
  <Typography as="span" bold color="#FFFFFF">
    Professional&apos;s eligibility criteria:
    <br />
    <br />
    {'•'} Has at least a 95% attendance score for previous shifts worked.
    <br />
    {'•'} Has a minimum of 2 years working experience.
    <br />
    {'•'} Has received exceptional performance ratings on over 95% of completed shifts.
    <br />
    {'•'} Has completed at least 3 shifts.
  </Typography>
);

export const SpeedUpCoverageSection = ({ control, watch }: ISpeedUpCoverageSectionProps) => {
  const professionType = watch('professionType');
  const preferredSpecialty = watch('preferredSpecialty');
  const officeId = watch('officeId');
  const inviteIds = useWatch({ control, name: 'invite' }) ?? [];

  const { setValue } = useFormContext<IShiftDetailsFormValues>();
  const locked = !useHasValidPaymentMethod(officeId);

  // Tracks whether the invite list has no professionals. When locked, the invite
  // card stays active in the empty state (nothing to interact with) but is dimmed
  // when there is a populated list to lock.
  const [inviteEmpty, setInviteEmpty] = useState(false);

  // When payment becomes invalid, the invite list and auto-confirm are gated.
  // Clear any selection / auto-confirm captured beforehand so no stale, locked
  // values are submitted. Only runs while locked, never overriding user input
  // once payment is valid again.
  useEffect(() => {
    if (!locked) return;
    setValue('invite', [], { shouldDirty: false });
    setValue('inviteNames', {}, { shouldDirty: false });
    setValue(
      'autoConfirm',
      { favorites: false, eligibleProfessionals: false },
      { shouldDirty: false },
    );
  }, [locked, setValue]);

  return (
    <Column data-testid="speed-up-coverage-section" data-cy="speed-up-coverage-section" gap={3}>
      {locked ? (
        <Box style={{ marginTop: -8 }}>
          <PaymentLockedBanner context="invite" />
        </Box>
      ) : (
        inviteIds.length === 0 && (
          <Box style={{ marginTop: -8 }}>
            <TipBanner
              visible
              description="Secure coverage with ease! Shifts with invites fill 2x faster."
            />
          </Box>
        )
      )}

      <InviteSubCard
        title="Invite Professionals:"
        tooltip={INVITE_TOOLTIP}
        testId="invite-sub-card"
        disabled={locked && !inviteEmpty}
      >
        <InviteField
          control={control}
          professionType={professionType}
          officeId={officeId}
          disabled={locked}
          onListEmptyChange={setInviteEmpty}
        />
      </InviteSubCard>

      <InviteSubCard
        title="Auto-Confirm:"
        tooltip={AUTO_CONFIRM_TOOLTIP}
        testId="auto-confirm-sub-card"
        disabled={locked}
      >
        <AutoConfirmField
          control={control}
          professionType={professionType}
          preferredSpecialty={preferredSpecialty}
          eventContext="shift_request"
          hideHeader
          disabled={locked}
        />
      </InviteSubCard>
    </Column>
  );
};
